import { useCurrentWorkspace, useRoutineAlerts } from '@gripp/shared-logic';
import {
  AlertIcon,
  ColumnData,
  NumberTag,
  SlideAction,
  SlideLayout,
  SlideLayoutLocationState,
  useSlideLayoutSafe,
  ViewMode,
} from '@gripp/shared-ui';
import { Pressable, StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RoutinePreviewColumn } from '../../new-routines/components/routinePreviewColumn';
import { ProductDetail } from './productDetail';

type RoutineSubmitLocationState = SlideLayoutLocationState & {
  from?: string;
};

export const ProductRoutineSubmit = () => {
  const { productId = '', routineId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const slideLayout = useSlideLayoutSafe();
  const currentWorkspace = useCurrentWorkspace();
  const locationState = location.state as RoutineSubmitLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  const { routineWorkflows, isLoading: areAlertsLoading } = useRoutineAlerts(
    '',
    routineId,
    productId,
    { enabled: !!currentWorkspace }
  );

  const from = locationState?.from;
  const cameFromRoutinesList = from === 'routines-list';

  if (!productId || !routineId) return null;

  const backPath = cameFromRoutinesList
    ? `/products/${productId}/routines/all`
    : `/products/${productId}`;

  const alertsCount = routineWorkflows?.length ?? 0;
  const isReady = !areAlertsLoading;
  const hasAlerts = alertsCount > 0;

  const onAlertPress = () => {
    const goToNewAlert = isReady && !hasAlerts;
    const target = goToNewAlert
      ? `/products/${productId}/alerts/${routineId}/new`
      : `/products/${productId}/alerts/${routineId}`;

    slideLayout?.prepareSlideTransition(SlideAction.NEXT);
    navigate(target, {
      state: {
        from,
        slideLayoutInitialTransition: SlideAction.NEXT,
        ...(goToNewAlert ? { fromRoutine: true } : {}),
      },
    });
  };

  const leftColumn: ColumnData = {
    title: 'product.list.products',
    mainComponent: <ProductDetail productId={productId} />,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonTransitionExitColumn: 'both',
    onBackButtonPress: () =>
      navigate(backPath, {
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      }),
    disableScroll: false,
  };

  const rightColumn: ColumnData = {
    title: 'product.detail.routines',
    headerComponent: (
      <Pressable onPress={onAlertPress} accessibilityRole="button">
        <View style={styles.alertIconContainer}>
          <AlertIcon size={24} />
          {alertsCount > 0 && (
            <View style={styles.badgeContainer}>
              <NumberTag number={alertsCount} variant="black" />
            </View>
          )}
        </View>
      </Pressable>
    ),
    mainComponent: (
      <RoutinePreviewColumn routineId={routineId} basePath={backPath} />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    onBackButtonPress: () =>
      navigate(backPath, {
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      }),
    disableScroll: true,
  };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};

const styles = StyleSheet.create({
  alertIconContainer: {
    position: 'relative',
  },
  badgeContainer: {
    position: 'absolute',
    top: -6,
    right: -6,
  },
});
